import { FETCH_ROLES } from '@/components/App/actionTypes'
import { EXPIRED_SESSION_FAILURE } from '@/components/AuthenticationHandler/actionTypes'

import { AppThunk } from '.'
import { CHANGE_CURRENT_SPACE, FETCH_USER, REQUEST, REQUEST_LIMIT } from './actionTypes'
import { getRoles } from './selectors'

type RequestParams = {
  body?: string | object
  headers?: object
  method?: string
  search?: object
}

type RequestOptions = {
  args?: object
  cached?: boolean
  ignoreLatest?: boolean
  service?: string | boolean
  promisify?: boolean
  ignoreLeading?: boolean
  setAuthorization?: boolean
  shouldNotClearErorr?: boolean
}

/**
 * Action creator for an API request using fetch.
 *
 * @param url - URL of the API request to fetch. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param actionType - Action type name without trailing underscore to be prefixed to `REQUEST`, `SUCCESS` and `ERROR` dispatched actions.
 * @param [params] - Map of parameters to pass to fetch for the API request.
 * @param [params.body] - Body of the request for applicable HTTP methods. Automatically serialized for JSON content typed requests. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param [params.headers] - Map of HTTP headers for the request.
 * @param [params.method=GET] - HTTP method of the request.
 * @param [params.search] - Map of query string parameters to append to the URL. Automatically encoded and separated. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param [options] - Map of configuration options to use for the API request.
 * @param [options.args] - Map of arguments to include in dispatched actions. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param [options.cached] - If the response for this request is already cached and does not need to be re-fetched.
 * @param [options.ignoreLatest=false] - If a request is already pending for this action type, ignore this request.
 * @param [options.service=tenant] - Name of the Workgrid service to make the API request to.
 * @param [options.promisify=false] - boolean flag to determine if request should return a promise.
 * @param [options.shouldNotClearError=false] - Clear the errors in _meta when the request is successful
 * @param additionalActionParams - Additonal params to include in the action dispatch which will be proxied to reducer but not used in the request
 * @return Fetch action to dispatch.
 */
export const request = (
  url: string,
  actionType: string,
  params: RequestParams = {},
  options: RequestOptions = {},
  additionalActionParams: any = {}
) => ({
  actionType,
  params,
  options,
  type: REQUEST,
  url,
  additionalActionParams
})

export const requestLimit = (requests: unknown, limit = 1) => ({ limit, requests, type: REQUEST_LIMIT })

export const createAction = (actionType: string, actionArgs: any = {}) => {
  // arg of `type` conflicts with Redux thunk action name that reducers rely on
  if (actionArgs.type) {
    delete actionArgs.type
  }

  return { type: actionType, ...actionArgs }
}

export const createActionWithError = (actionType: string, actionArgs: any, error: any) => {
  return error.code === 401 ? { type: EXPIRED_SESSION_FAILURE } : { type: actionType, error, ...actionArgs }
}

export function changeCurrentSpace(spaceId: string): AppThunk {
  return dispatch => {
    return new Promise(resolve => {
      resolve(dispatch(createAction(CHANGE_CURRENT_SPACE, { spaceId })))
    })
      .then(() => ({ spaceId }))
      .catch(() => ({ error: 'error' }))
  }
}

export const fetchUser = (userId: string, ignoreLatest = false): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        `/organizations/:organizationId/users/${userId}`,
        FETCH_USER,
        { method: 'GET' },
        { args: { organizationId: ':organizationId' }, ignoreLatest }
      )
    )
  }
}

type FetchUsersPaginatedBody = {
  count?: number
  filter?: any[]
  sortBy?: string
  sortOrder?: string
  spaceId?: string
}

/**
 * Action creator user search fetch.
 *
 * @param body - Options for user search
 * @param [body.count] - Maximum number of resources to return.
 * @param [body.filter] - SCIM filter statement to limit resources.
 * @param [body.sortBy] - SCIM attribute name to sort resources by.
 * @param [body.sortOrder=ascending] - Directionality to sort resources by.
 * @param [body.spaceId] - Options for user search
 * "count": 10,
 *   "filter": [
 *     { "type": "guid", "value": "a289ee79-b050-4981-9268-70c180868790" },
 *     { "type": "email", "value": "nick@workgrid.com" },
 *     { "type": "filter", "value": "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:department eq \"Workgrid Software\"" }
 *   ],
 *   "sortBy": "name.familyName",
 *   "sortOrder": "ascending",
 *   "spaceId": "space1"
 * @param actionType - Action type for component using this action. Expected to begin with "organization/users"
 * @param [args] - arguments to pass to reducer
 * @return Fetch action to dispatch.
 */
export const fetchUsersPaginated = (body: FetchUsersPaginatedBody, actionType: string, args: object = {}): AppThunk => {
  return dispatch => {
    return dispatch(
      request(
        '/organizations/:organizationId/users/search',
        actionType,
        { method: 'POST', body },
        { args: { organizationId: ':organizationId', ...args }, ignoreLeading: true }
      )
    )
  }
}

export function resetRequest(request: string | string[]): AppThunk {
  return dispatch => {
    request = Array.isArray(request) ? request : [request]
    request.forEach(actionType => dispatch(createAction(`${actionType}_RESET`)))
  }
}

export function resetRequests(requests: string | string[]) {
  return resetRequest(requests)
}

export const fetchRoles = (): AppThunk => {
  return (dispatch, getState) => {
    return dispatch(request('/roles', FETCH_ROLES, {}, { cached: getRoles(getState()).length > 0 }))
  }
}
